import React, { useState } from "react";
import useSWR from "swr";
import {
  Table,
  Drawer,
  Descriptions,
  Select,
  Input,
  Button,
  Space,
  Tag,
  message,
  Popconfirm,
  Divider,
  Alert,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  getCommunityCustomers,
  getCommunityCustomerDetail,
  issueCommunitySanction,
  revokeCommunitySanction,
  setCommunityCustomerBlock,
} from "../api/domainApi";
import {
  BlockReason,
  CmntyCustomerListItem,
  CmntySanctionItem,
  SanctionReasonCode,
} from "../types/domain";
import PageHeader from "../components/common/PageHeader";

const PAGE_SIZE = 20;
const STRIKE_LIMIT = 3; // 서버와 동일 — 안내용

// 제재 사유 — 서버 SanctionReasonCode enum 과 1:1
const REASON_LABEL: Record<SanctionReasonCode, string> = {
  ABUSE: "욕설/비방",
  SPAM: "스팸/광고",
  HARASS: "괴롭힘",
  INFO_FALSE: "허위정보",
  ETC: "기타",
};
const REASON_OPTIONS = (Object.keys(REASON_LABEL) as SanctionReasonCode[]).map((r) => ({
  value: r,
  label: REASON_LABEL[r],
}));

// 차단 출처 — 서버 BlockReason enum 과 1:1
const BLOCK_REASON_LABEL: Record<BlockReason, string> = {
  AUTO_STRIKE: "삼진아웃",
  MANUAL: "수동 차단",
};

const BLOCK_OPTIONS = [
  { value: true, label: "차단" },
  { value: false, label: "정상" },
];

const fmt = (v?: string | null, len = 16) => (v ? v.replace("T", " ").slice(0, len) : "-");

const CommunityCustomerPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [nickname, setNickname] = useState("");
  const [nkInput, setNkInput] = useState("");
  const [blocked, setBlocked] = useState<boolean | undefined>();

  const [detailUserId, setDetailUserId] = useState<number | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // 제재 부여 폼
  const [reasonCode, setReasonCode] = useState<SanctionReasonCode | undefined>();
  const [reasonDetail, setReasonDetail] = useState("");
  const [issuing, setIssuing] = useState(false);

  const swrKey = `/community/customers?p=${page}&nk=${nickname}&bk=${blocked}`;
  const { data, isLoading, mutate } = useSWR(
    swrKey,
    () =>
      getCommunityCustomers({
        page: page - 1,
        size: PAGE_SIZE,
        nickname: nickname || undefined,
        blocked,
      }),
    { keepPreviousData: true },
  );

  const { data: detail, mutate: mutateDetail } = useSWR(
    detailUserId ? `/community/customers/${detailUserId}` : null,
    () => getCommunityCustomerDetail(detailUserId!),
  );

  const openDetail = (row: CmntyCustomerListItem) => {
    setDetailUserId(row.userId);
    setReasonCode(undefined);
    setReasonDetail("");
    setDrawerOpen(true);
  };

  const refresh = () => {
    mutate();
    mutateDetail();
  };

  const act = async (fn: () => Promise<any>, ok: string) => {
    try {
      await fn();
      message.success(ok);
      refresh();
    } catch (e: any) {
      message.error(e.message);
    }
  };

  const handleIssue = async () => {
    if (!detail || !reasonCode) {
      message.warning("제재 사유를 선택하세요.");
      return;
    }
    setIssuing(true);
    try {
      await issueCommunitySanction(detail.userId, {
        reasonCode,
        reasonDetail: reasonDetail || undefined,
      });
      message.success("제재가 부여되었습니다.");
      setReasonCode(undefined);
      setReasonDetail("");
      refresh();
    } catch (e: any) {
      message.error(e.message);
    } finally {
      setIssuing(false);
    }
  };

  const columns: ColumnsType<CmntyCustomerListItem> = [
    { title: "user_id", dataIndex: "userId", width: 90 },
    { title: "고객명", dataIndex: "customerName", width: 120, render: (v) => v || "-" },
    { title: "별명", dataIndex: "nickname", ellipsis: true },
    {
      title: "누적 제재",
      dataIndex: "strikeCount",
      width: 100,
      render: (v: number) =>
        v > 0 ? <Tag color={v >= STRIKE_LIMIT ? "red" : "orange"}>{v} / {STRIKE_LIMIT}</Tag> : <Tag>0</Tag>,
    },
    {
      title: "상태",
      dataIndex: "blocked",
      width: 90,
      render: (v: boolean) => (v ? <Tag color="red">차단</Tag> : <Tag color="green">정상</Tag>),
    },
    {
      title: "가입일",
      dataIndex: "createdAt",
      width: 130,
      render: (v: string) => fmt(v),
    },
    {
      title: "관리",
      width: 90,
      render: (_, row) => (
        <Button size="small" type="dashed" onClick={() => openDetail(row)}>
          상세
        </Button>
      ),
    },
  ];

  const reset = () => {
    setNickname("");
    setNkInput("");
    setBlocked(undefined);
    setPage(1);
  };

  // 철회 시 차단이 어떻게 되는지 — 서버 동작(AUTO_STRIKE 만 자동 해제)과 일치시킨다.
  const revokeHint = (): string | undefined => {
    if (!detail?.blocked) return undefined;
    if (detail.blockedReason === "MANUAL") {
      return "수동 차단은 유지됩니다. 해제하려면 아래 「차단 해제」를 사용하세요.";
    }
    if (detail.blockedReason === "AUTO_STRIKE" && detail.strikeCount - 1 < STRIKE_LIMIT) {
      return `누적 제재가 ${STRIKE_LIMIT}회 미만이 되어 삼진아웃 차단이 자동 해제됩니다.`;
    }
    return undefined; // 여전히 3회 이상이거나 출처 불명 → 차단 유지
  };

  const sanctionColumns: ColumnsType<CmntySanctionItem> = [
    { title: "ID", dataIndex: "sanctionId", width: 60 },
    {
      title: "사유",
      dataIndex: "reasonCode",
      width: 90,
      render: (v: SanctionReasonCode) => REASON_LABEL[v] ?? v,
    },
    { title: "상세", dataIndex: "reasonDetail", ellipsis: true, render: (v) => v || "-" },
    { title: "처리자", dataIndex: "createdId", width: 110, render: (v) => v || "-" },
    { title: "일시", dataIndex: "createdAt", width: 140, render: (v: string) => fmt(v) },
    {
      title: "",
      width: 70,
      render: (_, s) => (
        <Popconfirm
          title="이 제재를 철회하시겠습니까?"
          description={revokeHint()}
          onConfirm={() => act(() => revokeCommunitySanction(s.sanctionId), "제재가 철회되었습니다.")}
        >
          <Button size="small" danger>
            철회
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      <PageHeader title="커뮤니티 고객 관리" />
      <Space style={{ marginBottom: 16 }} wrap>
        <Input
          placeholder="별명 검색"
          value={nkInput}
          onChange={(e) => setNkInput(e.target.value)}
          onPressEnter={() => {
            setNickname(nkInput);
            setPage(1);
          }}
          style={{ width: 200 }}
        />
        <Button
          onClick={() => {
            setNickname(nkInput);
            setPage(1);
          }}
        >
          검색
        </Button>
        <Select
          placeholder="상태 전체"
          allowClear
          style={{ width: 120 }}
          options={BLOCK_OPTIONS}
          value={blocked}
          onChange={(v) => {
            setBlocked(v);
            setPage(1);
          }}
        />
        <Button onClick={reset}>초기화</Button>
      </Space>
      <Table
        rowKey="userId"
        columns={columns}
        dataSource={data?.content}
        loading={isLoading}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: data?.page?.totalElements,
          onChange: setPage,
          showSizeChanger: false,
        }}
        size="small"
      />

      <Drawer
        title={`고객 상세 — user #${detail?.userId ?? ""}`}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={720}
      >
        {detail && (
          <>
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="고객명">{detail.customerName || "-"}</Descriptions.Item>
              <Descriptions.Item label="이메일">{detail.email || "-"}</Descriptions.Item>
              <Descriptions.Item label="별명">{detail.nickname}</Descriptions.Item>
              <Descriptions.Item label="프로필 ID">{detail.profileId}</Descriptions.Item>
              <Descriptions.Item label="누적 제재">
                <Tag color={detail.strikeCount >= STRIKE_LIMIT ? "red" : detail.strikeCount > 0 ? "orange" : "default"}>
                  {detail.strikeCount} / {STRIKE_LIMIT}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="상태">
                {detail.blocked ? (
                  <Space size={4}>
                    <Tag color="red">차단</Tag>
                    {detail.blockedReason && (
                      <Tag color={detail.blockedReason === "MANUAL" ? "purple" : "volcano"}>
                        {BLOCK_REASON_LABEL[detail.blockedReason]}
                      </Tag>
                    )}
                  </Space>
                ) : (
                  <Tag color="green">정상</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="작성 글">{detail.postCount}</Descriptions.Item>
              <Descriptions.Item label="작성 댓글">{detail.commentCount}</Descriptions.Item>
              <Descriptions.Item label="가입일" span={2}>{fmt(detail.createdAt, 19)}</Descriptions.Item>
            </Descriptions>

            <Divider titlePlacement="left">제재 이력 ({detail.sanctions.length})</Divider>
            <Table
              rowKey="sanctionId"
              columns={sanctionColumns}
              dataSource={detail.sanctions}
              pagination={false}
              size="small"
              locale={{ emptyText: "제재 이력 없음" }}
            />

            <Divider titlePlacement="left">제재 부여 (1건 부여 시 누적 +1, {STRIKE_LIMIT}회 도달 시 자동 차단)</Divider>
            {detail.blocked ? (
              <Alert
                type="info"
                showIcon
                message="이미 차단된 고객입니다."
                description="추가 제재를 하려면 먼저 아래에서 차단을 해제하세요."
              />
            ) : (
              <Space direction="vertical" style={{ width: "100%" }} size="middle">
                <Space>
                  <span>사유</span>
                  <Select
                    placeholder="제재 사유"
                    style={{ width: 160 }}
                    options={REASON_OPTIONS}
                    value={reasonCode}
                    onChange={setReasonCode}
                  />
                </Space>
                <Input.TextArea
                  rows={2}
                  placeholder="사유 상세 (선택, 최대 500자)"
                  maxLength={500}
                  value={reasonDetail}
                  onChange={(e) => setReasonDetail(e.target.value)}
                />
                <Popconfirm
                  title="제재를 부여하시겠습니까?"
                  description={
                    detail.strikeCount + 1 >= STRIKE_LIMIT
                      ? `이 제재로 누적 ${STRIKE_LIMIT}회에 도달하여 고객이 차단됩니다.`
                      : undefined
                  }
                  onConfirm={handleIssue}
                  disabled={!reasonCode}
                >
                  <Button type="primary" danger loading={issuing} disabled={!reasonCode}>
                    제재 부여
                  </Button>
                </Popconfirm>
              </Space>
            )}

            <Divider titlePlacement="left">차단 관리 (삼진아웃과 별개, 즉시 override)</Divider>
            {detail.blocked ? (
              <Popconfirm
                title="차단을 해제하시겠습니까?"
                description={
                  detail.strikeCount >= STRIKE_LIMIT
                    ? `누적 제재가 ${detail.strikeCount}회로 남아 있어, 이후 제재 1건이면 다시 자동 차단됩니다.`
                    : undefined
                }
                onConfirm={() =>
                  act(() => setCommunityCustomerBlock(detail.userId, { blocked: false }), "차단이 해제되었습니다.")
                }
              >
                <Button>차단 해제</Button>
              </Popconfirm>
            ) : (
              <Popconfirm
                title="이 고객을 즉시 차단하시겠습니까?"
                description="커뮤니티 접근(읽기 포함)이 제한됩니다. 수동 차단은 제재 철회로 자동 해제되지 않습니다."
                onConfirm={() =>
                  act(() => setCommunityCustomerBlock(detail.userId, { blocked: true }), "고객이 차단되었습니다.")
                }
              >
                <Button danger>즉시 차단</Button>
              </Popconfirm>
            )}
          </>
        )}
      </Drawer>
    </div>
  );
};

export default CommunityCustomerPage;
